import { Block, PartialBlock } from "@blocknote/core";
import * as Sentry from "@sentry/react";

export function isBlockNoteEmpty(blocks: PartialBlock[]): boolean {
  if (!blocks.length) return true;

  if (blocks.length > 1) return false;

  const firstBlock = blocks[0];
  if (firstBlock.type !== "paragraph") return false;

  const content = firstBlock.content;
  if (!content) return true;

  if (typeof content === "string") return content === "";

  if (Array.isArray(content)) {
    return (
      content.length === 0 ||
      (content.length === 1 &&
        typeof content[0] === "object" &&
        "type" in content[0] &&
        content[0].type === "text" &&
        "text" in content[0] &&
        content[0].text === "")
    );
  }

  return false;
}

function extractPlainText(blocks: Block[]): string {
  try {
    let text = "";
    for (const block of blocks) {
      const content = block.content;

      if (Array.isArray(content)) {
        for (const inline of content) {
          if (inline.type === "text") {
            text += inline.text;
          } else if (inline.type === "link" && inline.content) {
            for (const nested of inline.content) {
              if (nested.type === "text") {
                text += nested.text;
              }
            }
          }
        }
      }

      if (block.children) {
        text += extractPlainText(block.children);
      }

      text += "\n";
    }
    return text.trim();
  } catch (e) {
    // Make sure unexpected blocks do not crash the app
    Sentry.captureException(e);
    return "";
  }
}

function countWords(text: string): number {
  return text.trim() ? text.trim().split(/\s+/).length : 0;
}

function countCharacters(text: string): number {
  return text.replace(/\s/g, "").length;
}

export function getWordCount(blocks: Block[]): number {
  const text = extractPlainText(blocks);
  return countWords(text);
}

export function getCharacterCount(blocks: Block[]): number {
  const text = extractPlainText(blocks);
  return countCharacters(text);
}

export function getWordAndCharacterCount(blocks: Block[]): { words: number; characters: number } {
  const text = extractPlainText(blocks);
  return {
    words: countWords(text),
    characters: countCharacters(text),
  };
}
